<template>
  <div class="base-input-field" :class="[inputModeClass, { disabled }]">
    <input
      ref="inputEl"
      type="text"
      :id="id"
      :inputmode="inputmode"
      :pattern="inputPattern"
      :name="name"
      :autocomplete="autocomplete"
      :autocapitalize="autocapitalize"
      :spellcheck="spellcheck"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :maxlength="maxlength"
      :data-autofocus="autofocus ? 'true' : null"
      :aria-labelledby="labelledBy"
      :aria-describedby="ariaDescribedby"
      :aria-invalid="ariaInvalid || undefined"
      :role="role"
      :aria-autocomplete="ariaAutocomplete"
      :aria-expanded="ariaExpanded"
      :aria-controls="ariaControls"
      :aria-activedescendant="ariaActivedescendant"
      @beforeinput="onBeforeInput"
      @input="onInput"
      @focus="emit('focus')"
      @blur="emit('blur')"
      @keydown="onKeydown"
    />
    <div v-if="innerLabelData || innerLabelFallback" :id="innerLabelId" class="inner-label">
      <TipTap v-if="innerLabelData" v-model="innerLabelData" mode="single" />
      <span v-else>{{ innerLabelFallback }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, useId } from 'vue'
import TipTap from '@render/lib/TipTap'

const props = withDefaults(
  defineProps<{
    modelValue?: number | string
    innerLabelFallback?: string
    /**
     * Ids of the elements naming this field — normally the host's outer label.
     * Declared as real props (rather than left to fall through) because this
     * component's root is a wrapper <div>: a fallthrough `aria-labelledby`
     * would land there, where nothing reads it, instead of on the <input>.
     * Same reason `PlainImage` declares `alt`.
     */
    ariaLabelledby?: string
    /** Ids of the validation / hint text describing this field. */
    ariaDescribedby?: string
    ariaInvalid?: boolean
    /**
     * Combobox wiring, for a host rendering an autocomplete popup below the
     * field (EmailInput). Passed through as one group because the pattern only
     * works whole: the popup's `role="listbox"` is useless unless the INPUT
     * says it is expanded and which option is active — a screen reader reads
     * the active option off the focused input, never off the list.
     */
    role?: string
    ariaAutocomplete?: 'none' | 'inline' | 'list' | 'both'
    ariaExpanded?: boolean
    ariaControls?: string
    ariaActivedescendant?: string
    placeholder?: string
    disabled?: boolean
    autofocus?: boolean
    maxlength?: number
    inputmode?: 'text' | 'numeric' | 'search' | 'email' | 'tel' | 'url' | 'none' | 'decimal'
    id?: string
    name?: string
    autocomplete?: string
    autocapitalize?: 'off' | 'none' | 'on' | 'sentences' | 'words' | 'characters'
    spellcheck?: boolean
    stripWhitespace?: boolean
    textColor?: string
    textColorPlaceholder?: string
    textColorDisabled?: string
    labelColor?: string
    labelColorDisabled?: string
    labelFontSize?: string
    labelLineHeight?: string
    labelFontWeight?: string
    numbersModeFontSize?: string
    numbersModeLineHeight?: string
    numbersModeFontWeight?: string
    textModeFontSize?: string
    textModeLineHeight?: string
    textModeFontWeight?: string
  }>(),
  {
    modelValue: '',
    innerLabelFallback: undefined,
    ariaLabelledby: undefined,
    ariaDescribedby: undefined,
    ariaInvalid: false,
    role: undefined,
    ariaAutocomplete: undefined,
    ariaExpanded: undefined,
    ariaControls: undefined,
    ariaActivedescendant: undefined,
    placeholder: undefined,
    disabled: false,
    autofocus: false,
    maxlength: undefined,
    inputmode: undefined,
    id: undefined,
    name: undefined,
    autocomplete: undefined,
    autocapitalize: undefined,
    spellcheck: undefined,
    stripWhitespace: false,
    textColor: undefined,
    textColorPlaceholder: undefined,
    textColorDisabled: undefined,
    labelColor: undefined,
    labelColorDisabled: undefined,
    labelFontSize: undefined,
    labelLineHeight: undefined,
    labelFontWeight: undefined,
    numbersModeFontSize: undefined,
    numbersModeLineHeight: undefined,
    numbersModeFontWeight: undefined,
    textModeFontSize: undefined,
    textModeLineHeight: undefined,
    textModeFontWeight: undefined,
  },
)

const innerLabelData = defineModel<object>('innerLabelData')

// The inner label is the unit ("years", "ft", "in") and it is part of the
// field's name, not a description — HeightInput's imperial mode renders TWO
// fields inside ONE <label> shell, so without it both announce the same
// ambiguous name. Appending it to the host's label keeps "How tall are you?
// ft" and "How tall are you? in" distinguishable.
const innerLabelId = `${useId()}-unit`
const hasInnerLabel = computed(() => !!(innerLabelData.value || props.innerLabelFallback))
const labelledBy = computed(() => {
  const ids = [props.ariaLabelledby, hasInnerLabel.value ? innerLabelId : null].filter(Boolean)
  // Nothing to point at — leave the attribute off so the wrapping <label>
  // keeps naming the field.
  return ids.length ? ids.join(' ') : undefined
})

const emit = defineEmits<{
  'update:modelValue': [value: number | string]
  blur: []
  focus: []
  keydown: [event: KeyboardEvent]
  submit: []
}>()

const onKeydown = (e: KeyboardEvent) => {
  emit('keydown', e)
  // Fire submit on Enter (desktop) / "GO" on Android. The parent's keydown handler can call e.preventDefault() to prevent submit
  // EmailInput picking an autocomplete suggestion), we skip submit.
  if (e.key === 'Enter' && !e.defaultPrevented) {
    emit('submit')
  }
}

// Auto-derive pattern from inputmode so mobile browsers pick the right keyboard
const inputPatterns: Partial<Record<string, string>> = {
  numeric: '[0-9]*',
  decimal: '[0-9]*[.,]?[0-9]*',
  tel: '[0-9+\\-\\s]*',
}
const inputPattern = computed(() => (props.inputmode ? inputPatterns[props.inputmode] : undefined))

const inputModeClass = computed(() => {
  const isNumbersMode = props.inputmode === 'numeric' || props.inputmode === 'decimal'
  return isNumbersMode ? 'numbers-mode' : 'text-mode'
})

const overrideTextColor = computed(() => props.textColor || 'var(--color-input-text-accent)')
const overrideTextColorPlaceholder = computed(
  () => props.textColorPlaceholder || 'var(--color-input-text-placeholder)',
)
const overrideTextColorDisabled = computed(
  () => props.textColorDisabled || 'var(--color-input-text-disabled)',
)
const overrideLabelColor = computed(() => props.labelColor || 'var(--color-text-secondary)')
const overrideLabelColorDisabled = computed(
  () => props.labelColorDisabled || 'var(--color-input-text-disabled)',
)
const overrideLabelFontSize = computed(() => props.labelFontSize || 'var(--text-body-sm-size)')
const overrideLabelLineHeight = computed(
  () => props.labelLineHeight || 'var(--text-body-sm-line-height)',
)
const overrideLabelFontWeight = computed(
  () => props.labelFontWeight || 'var(--text-body-sm-weight)',
)
const overrideNumbersModeFontSize = computed(
  () => props.numbersModeFontSize || 'var(--text-heading-1-size)',
)
const overrideNumbersModeLineHeight = computed(
  () => props.numbersModeLineHeight || 'var(--text-heading-1-line-height)',
)
const overrideNumbersModeFontWeight = computed(
  () => props.numbersModeFontWeight || 'var(--text-heading-1-weight)',
)
const overrideTextModeFontSize = computed(
  () => props.textModeFontSize || 'var(--text-body-lg-size)',
)
const overrideTextModeLineHeight = computed(
  () => props.textModeLineHeight || 'var(--text-body-lg-line-height)',
)
const overrideTextModeFontWeight = computed(
  () => props.textModeFontWeight || 'var(--text-body-lg-weight)',
)

const inputEl = ref<HTMLInputElement | null>(null)

function onBeforeInput(e: InputEvent) {
  if (!props.stripWhitespace) return
  if (e.inputType === 'insertText' && e.data && /\s/.test(e.data)) {
    e.preventDefault()
    return
  }
  if (e.inputType === 'insertFromPaste' && e.data && /\s/.test(e.data)) {
    e.preventDefault()
    const cleaned = e.data.replace(/\s/g, '')
    if (!cleaned) return
    const input = e.target as HTMLInputElement
    const start = input.selectionStart ?? input.value.length
    const end = input.selectionEnd ?? input.value.length
    input.setRangeText(cleaned, start, end, 'end')
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }
}

function onInput(e: Event) {
  const input = e.target as HTMLInputElement

  if (props.inputmode === 'numeric') {
    input.value = input.value.replace(/[^\d\u0660-\u0669]/g, '')
    const num = parseInt(input.value, 10)
    emit('update:modelValue', isNaN(num) ? '' : num)
  } else {
    emit('update:modelValue', input.value)
  }
}

defineExpose({ focus: () => inputEl.value?.focus() })
</script>

<style scoped lang="scss">
@supports (-moz-appearance: none) {
  input,
  textarea,
  select {
    touch-action: manipulation;
  }
}

.base-input-field {
  display: flex;
  align-items: baseline;
  flex: 1;
  min-width: 0;
  gap: var(--space-12);

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: v-bind(overrideTextColor);

    &::placeholder {
      color: v-bind(overrideTextColorPlaceholder);
    }

    &:disabled {
      color: v-bind(overrideTextColorDisabled);

      &::placeholder {
        color: v-bind(overrideTextColorDisabled);
      }
    }
  }

  .inner-label {
    flex-shrink: 0;
    color: v-bind(overrideLabelColor);
    font-family: var(--text-body-sm-family);
    font-weight: v-bind(overrideLabelFontWeight);
    font-size: v-bind(overrideLabelFontSize);
    line-height: v-bind(overrideLabelLineHeight);
    user-select: none;
  }

  &.disabled .inner-label {
    color: v-bind(overrideLabelColorDisabled);
  }

  &.numbers-mode input {
    font-family: var(--text-heading-1-family);
    font-weight: v-bind(overrideNumbersModeFontWeight);
    font-size: max(16px, v-bind(overrideNumbersModeFontSize));
    line-height: v-bind(overrideNumbersModeLineHeight);
    padding: 0;
  }

  &.text-mode input {
    font-family: var(--text-body-lg-family);
    font-weight: v-bind(overrideTextModeFontWeight);
    font-size: max(16px, v-bind(overrideTextModeFontSize));
    line-height: v-bind(overrideTextModeLineHeight);
  }
}
</style>
